import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OptimizedImage, TrustpilotWidget } from '../common';
import { BookingFormModal } from '../forms';

import styles from '../../styles/components/sections/HeroSection.module.css';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { useModal } from '../../utils/hooks/useModal';
// import heroImage from '../../public/assets/heroImage.png';
// import heroImageMobile from '../../public/assets/heroImageMobile.png';

import { isSafari } from '../../utils/helpers/utils';
import Image from 'next/image';

const HeroSection = ({ data, phoneNumber }) => {

    const { openModal } = useModal();
    const { isDarkMode, isThemeInitialized } = useDarkMode();
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const sectionRef = useRef(null);

    // Memoize expensive computations with enhanced data structure support
    const isBookingFormShown = useMemo(() => {
        // Check for EnhancedHeroSection structure first
        if (data?.get_quote || data?.call_button) {
            return true;
        }
        // Fallback to original structure
        return data?.isBookingFormShown ?? false;
    }, [data?.get_quote, data?.call_button, data?.isBookingFormShown]);
    
    const backgroundImage = useMemo(() => {
        // Check for EnhancedHeroSection structure first
        if (data?.Image?.image?.url) {
            return data.Image.image.url;
        }
        // Fallback to original structure
        return data?.Image?.image?.url || null;
    }, [data?.Image?.image?.url]);
    
    const mobileBackgroundImage = useMemo(() => {
        // Check for EnhancedHeroSection structure first
        if (data?.mobileImage?.image?.url) {
            return data.mobileImage.image.url;
        }
        // Fallback to original structure
        return data?.MobileImage?.image?.url || null;
    }, [data?.mobileImage?.image?.url, data?.MobileImage?.image?.url]);
    
    // Enhanced phone number handling
    const displayPhoneNumber = useMemo(() => {
        // Check for EnhancedHeroSection structure first
        if (data?.phone) {
            return { value: data.phone };
        }
        // Fallback to original structure
        return phoneNumber;
    }, [data?.phone, phoneNumber]);
    
    // Determine content type for appropriate height
    const contentType = useMemo(() => {
        // Check if this is a minimal content page (like FAQ)
        const hasMinimalContent = !isBookingFormShown && 
            (!data?.subtitle && !data?.subTitle) && 
            data?.title && 
            data.title.length < 100; // Short titles typically indicate minimal content
        
        // Check if this is very minimal content (just a title)
        const hasVeryMinimalContent = hasMinimalContent && 
            (!data?.subtitle && !data?.subTitle);
        
        if (hasVeryMinimalContent) return 'veryMinimalContent';
        if (hasMinimalContent) return 'minimalContent';
        if (isBookingFormShown) return 'withBookingForm';
        return 'default';
    }, [isBookingFormShown, data?.title, data?.subtitle, data?.subTitle]);

    // Section class - Use CSS classes for responsive behavior to prevent CLS
    // No inline styles that depend on JS state (isMobile, isThemeInitialized)
    const sectionClass = useMemo(() => {
        const classes = [
            'position-relative',
            'd-flex',
            'align-items-center',
            'text-left',
            styles.heroSection
        ];
        
        // Add content-specific class
        if (contentType === 'withBookingForm') {
            classes.push(styles.withBookingForm);
        } else if (contentType === 'minimalContent') {
            classes.push(styles.minimalContent);
        } else if (contentType === 'veryMinimalContent') {
            classes.push(styles.veryMinimalContent);
        }
        
        return classes.join(' ');
    }, [contentType]);
    
    // Container class - Use CSS classes for responsive behavior to prevent CLS
    const containerClass = useMemo(() => {
        const classes = [
            'tw-container',
            'h-100',
            'd-flex',
            'align-items-center',
            'justify-content-center',
            'mt-2',
            'gap-4',
            styles.heroContainer
        ];
        
        if (contentType === 'withBookingForm') {
            classes.push(styles.withBookingForm);
        }
        
        return classes.join(' ');
    }, [contentType]);
    
    // Row class - Use CSS for responsive behavior to prevent CLS
    const rowClass = useMemo(() => {
        const classes = ['row', styles.heroRow];
        
        if (contentType === 'withBookingForm') {
            classes.push(styles.withBookingForm);
        }
        
        return classes.join(' ');
    }, [contentType]);
    
    // Content container class - Use CSS for responsive behavior to prevent CLS
    const contentContainerClass = useMemo(() => {
        const classes = [
            'col-12',
            styles.heroContentContainer,
            styles.contentContainer
        ];
        
        if (contentType === 'withBookingForm') {
            classes.push(styles.withBookingForm);
        } else {
            classes.push('col-lg-6', 'tw-container');
        }
        
        return classes.join(' ');
    }, [contentType]);
    
    const headingClass = useMemo(() => {
        return contentType === 'withBookingForm'
            ? `${styles.heading} fw-bold mb-3 !text-center`
            : `${styles.heading} !w-full fw-bold mb-4 !text-center`;
    }, [contentType]);
    
    const subtitleClass = useMemo(() => {
        return contentType === 'withBookingForm'
            ? `${styles.subtitle} mb-4 text-center`
            : `${styles.subtitle} mb-5 !text-center`;
    }, [contentType]);

    // Optimized image loading - prevent reflows
    useEffect(() => {
        if (isThemeInitialized && backgroundImage) {
            // Use requestAnimationFrame to prevent forced reflows
            const loadImage = () => {
                // Image will load automatically via OptimizedImage component
                // No DOM queries or style changes here
            };
            
            requestAnimationFrame(loadImage);
        }
    }, [isThemeInitialized, backgroundImage]);

    const handleGetQuote = useCallback(() => {
        if (isBookingFormShown) {
            setIsBookingModalOpen(true);
        } else {
            openModal();
        }
    }, [isBookingFormShown, openModal]);

    const handleCloseBookingModal = useCallback(() => {
        setIsBookingModalOpen(false);
    }, []);

    const handleImageLoad = useCallback(() => {
        // Use requestAnimationFrame to prevent forced reflows
        requestAnimationFrame(() => {
            setIsImageLoaded(true);
        });
    }, []);

    const placeholderBackgroundColor = useMemo(() => (isDarkMode ? '#1a1a1a' : '#f8f9fa'), [isDarkMode]);
    const overlayBackgroundColor = useMemo(() => (isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'), [isDarkMode]);



    return (
        <section ref={sectionRef} className={sectionClass}>
            {/* Background Image */}
            <div className="position-absolute top-0 start-0 w-100 h-100 overflow-hidden">
                {/* {backgroundImage && (
                    <OptimizedImage
                        src={heroImageMobile}
                        mobileSrc={backgroundImage}
                        alt="Hero Section"
                        className="w-100 h-100"
                        style={{
                            objectFit: 'cover',
                            objectPosition: 'center',
                        }}
                        width={1920}
                        height={1080}
                        priority={true}
                        fill
                        loading="eager"
                        fetchPriority="high"
                        onLoad={handleImageLoad}
                        sizes="100vw"
                        quality={75}
                    />
                )} */}

                {/* Loading placeholder */}
                {backgroundImage && !isImageLoaded && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{
                            backgroundColor: placeholderBackgroundColor,
                        }}
                    />
                )}


                {/* Dark overlay */}
                <div
                    className={`position-absolute top-0 start-0 w-100 h-100 ${styles['image-overlay']} ${isDarkMode ? styles['dark'] : ''}`}
                    style={{
                        backgroundColor: overlayBackgroundColor,
                    }}></div>
            </div>

            <div className={containerClass}>
                <div className={rowClass}>
                    <div className={contentContainerClass}>
                        {/* Heading */}
                        <p 
                            className={headingClass} 
                            style={{ 
                                fontFamily: "'Gilroy-Bold', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                fontDisplay: 'swap',
                                textRendering: 'optimizeSpeed'
                            }}
                        >
                            {data?.title}
                        </p>

                        {/* Subtitle */}
                        <p 
                            className={subtitleClass} 
                            style={{ 
                                fontFamily: "'Gilroy-Regular', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                fontDisplay: 'swap',
                                textRendering: 'optimizeSpeed'
                            }}
                        >
                            {data?.subtitle ?? data?.subTitle}
                        </p>

                        {/* Buttons Section - Only show when booking form is shown */}
                        {contentType === 'withBookingForm' && (
                            <>
                                <div className="d-flex flex-column flex-sm-row align-items-center justify-content-center gap-4 mb-[2rem]">
                                    <button
                                        onClick={handleGetQuote}
                                        className={styles.heroButton}
                                        aria-label="Get quote - open booking form modal"
                                        type="button">
                                        {data?.get_quote?.name || "GET QUOTE"}
                                    </button>

                                    {displayPhoneNumber && (
                                        <a
                                            href={`tel:${displayPhoneNumber?.value}`}
                                            className={`${styles.heroButtonOutline} group`}
                                            aria-label={`Call us at ${displayPhoneNumber?.value}`}
                                            role="button">
                                            <OptimizedImage 
                                                src="https://images.hirego.co.uk/assets/Icons/Phone.svg" 
                                                alt="Call-us" 
                                                width={25} 
                                                height={25} 
                                                priority={false}
                                                loading="lazy"
                                                quality={65}
                                                sizes="25px"
                                                className="group-hover:brightness-0 transition-all duration-300"
                                            />
                                            {displayPhoneNumber?.value}
                                        </a>
                                    )}
                                </div>

                                {/* Trustpilot Section */}
                                <div className="d-flex align-items-center justify-content-center">
                                    <TrustpilotWidget 
                                        textColor="text-white"
                                        fontSize="text-sm"
                                        className="mb-0"
                                        rating="4.7"
                                        showIntroText={true}
                                        introText="Our Customers say:"
                                    />
                                </div>
                                {/* <div className="d-flex align-items-center justify-content-center">
                                    <span className={`me-3 text-white ${isDarkMode ? styles['dark-text'] : ''}`}>
                                        Our Customers say: Excellent 4.6 OUT OF 5 BASED ON
                                    </span>
                                    <OptimizedImage
                                        src="https://cdn.trustpilot.net/brand-assets/1.3.0/logo-white.svg"
                                        alt="Trustpilot"
                                        style={{
                                            height: '20px',
                                            filter: isDarkMode ? 'brightness(1.2) contrast(1.1)' : 'none',
                                            transition: 'filter var(--transition-duration) var(--transition-timing)',
                                        }}
                                        width={120}
                                        height={32}
                                        loading="lazy"
                                    />
                                </div> */}
                            </>
                        )}


                    </div> 
                </div>
            </div>

            {/* Booking Form Modal */}
            <BookingFormModal 
                isOpen={isBookingModalOpen} 
                onClose={handleCloseBookingModal}
                isDarkMode={isDarkMode}
            />
        </section>
    );
};

export default HeroSection;
